/**
 * Server-Sent Events (SSE) service for real-time data updates
 *
 * Provides a robust SSE connection manager with automatic reconnection,
 * event type handling, and connection state management.
 */

export type SSEConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error'

export interface SSEMessage<T = unknown> {
  type: string
  data: T
  id?: string
  timestamp?: number
}

export interface SSEOptions {
  /** Reconnection delay in milliseconds (default: 3000) */
  reconnectDelay?: number
  /** Maximum reconnection attempts (default: 5, -1 for infinite) */
  maxReconnectAttempts?: number
  /** Whether to automatically reconnect on error (default: true) */
  autoReconnect?: boolean
  /** Custom headers for the connection (via URL params since SSE doesn't support headers) */
  params?: Record<string, string>
}

type MessageHandler<T = unknown> = (message: SSEMessage<T>) => void
type StateHandler = (state: SSEConnectionState, error?: Error) => void

const DEFAULT_OPTIONS: Required<Omit<SSEOptions, 'params'>> = {
  reconnectDelay: 3000,
  maxReconnectAttempts: 5,
  autoReconnect: true
}

export class SSEService {
  private eventSource: EventSource | null = null
  private url: string
  private options: Required<Omit<SSEOptions, 'params'>> & Pick<SSEOptions, 'params'>
  private messageHandlers: Map<string, Set<MessageHandler>> = new Map()
  private stateHandlers: Set<StateHandler> = new Set()
  private reconnectAttempts = 0
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  private _state: SSEConnectionState = 'disconnected'

  constructor(url: string, options: SSEOptions = {}) {
    this.url = url
    this.options = { ...DEFAULT_OPTIONS, ...options }
  }

  get state(): SSEConnectionState {
    return this._state
  }

  private setState(state: SSEConnectionState, error?: Error): void {
    this._state = state
    this.stateHandlers.forEach(handler => handler(state, error))
  }

  /**
   * Connect to the SSE endpoint
   */
  connect(): void {
    if (this.eventSource?.readyState === EventSource.OPEN) {
      return
    }

    this.cleanup()
    this.setState('connecting')

    try {
      let connectionUrl = this.url
      if (this.options.params) {
        const params = new URLSearchParams(this.options.params)
        connectionUrl += (connectionUrl.includes('?') ? '&' : '?') + params.toString()
      }

      this.eventSource = new EventSource(connectionUrl)

      this.eventSource.onopen = () => {
        this.reconnectAttempts = 0
        this.setState('connected')
      }

      this.eventSource.onerror = () => {
        const error = new Error('SSE connection error')
        this.setState('error', error)
        this.handleReconnect()
      }

      this.eventSource.onmessage = (event) => {
        this.handleMessage(event)
      }

      // Register for specific event types that may be sent
      this.setupEventListeners()
    } catch (error) {
      this.setState('error', error instanceof Error ? error : new Error('Failed to connect'))
      this.handleReconnect()
    }
  }

  /**
   * Set up listeners for known event types
   */
  private setupEventListeners(): void {
    if (!this.eventSource) return

    // Listen for typed events (server sends with 'event: <type>')
    const eventTypes = ['fighter_update', 'event_update', 'fight_update', 'live_update']

    eventTypes.forEach(type => {
      this.eventSource!.addEventListener(type, (event: MessageEvent) => {
        this.handleTypedMessage(type, event)
      })
    })
  }

  /**
   * Handle generic messages (no event type specified)
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data)
      const message: SSEMessage = {
        type: data.type || 'message',
        data: data.data || data,
        id: event.lastEventId,
        timestamp: Date.now()
      }
      this.dispatchMessage(message)
    } catch {
      // If not JSON, treat as raw message
      const message: SSEMessage<string> = {
        type: 'message',
        data: event.data,
        id: event.lastEventId,
        timestamp: Date.now()
      }
      this.dispatchMessage(message)
    }
  }

  /**
   * Handle typed messages (event: <type> in SSE)
   */
  private handleTypedMessage(type: string, event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data)
      const message: SSEMessage = {
        type,
        data,
        id: event.lastEventId,
        timestamp: Date.now()
      }
      this.dispatchMessage(message)
    } catch {
      const message: SSEMessage<string> = {
        type,
        data: event.data,
        id: event.lastEventId,
        timestamp: Date.now()
      }
      this.dispatchMessage(message)
    }
  }

  /**
   * Dispatch message to registered handlers
   */
  private dispatchMessage(message: SSEMessage): void {
    // Dispatch to specific type handlers
    const typeHandlers = this.messageHandlers.get(message.type)
    typeHandlers?.forEach(handler => handler(message))

    // Dispatch to wildcard handlers
    const wildcardHandlers = this.messageHandlers.get('*')
    wildcardHandlers?.forEach(handler => handler(message))
  }

  /**
   * Handle reconnection logic
   */
  private handleReconnect(): void {
    this.cleanup()

    if (!this.options.autoReconnect) {
      this.setState('disconnected')
      return
    }

    if (this.options.maxReconnectAttempts !== -1 &&
        this.reconnectAttempts >= this.options.maxReconnectAttempts) {
      this.setState('disconnected')
      return
    }

    this.reconnectAttempts++
    const delay = this.options.reconnectDelay * Math.min(this.reconnectAttempts, 5)

    this.reconnectTimeout = setTimeout(() => {
      this.connect()
    }, delay)
  }

  /**
   * Register a handler for a specific message type
   * Use '*' to receive all messages
   */
  on<T = unknown>(type: string, handler: MessageHandler<T>): () => void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, new Set())
    }
    this.messageHandlers.get(type)!.add(handler as MessageHandler)

    // Return unsubscribe function
    return () => {
      this.messageHandlers.get(type)?.delete(handler as MessageHandler)
    }
  }

  /**
   * Register a handler for connection state changes
   */
  onStateChange(handler: StateHandler): () => void {
    this.stateHandlers.add(handler)
    // Immediately call with current state
    handler(this._state)

    return () => {
      this.stateHandlers.delete(handler)
    }
  }

  /**
   * Clean up event source and timeouts
   */
  private cleanup(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
    }
  }

  /**
   * Disconnect from the SSE endpoint
   */
  disconnect(): void {
    this.cleanup()
    this.reconnectAttempts = 0
    this.setState('disconnected')
  }

  /**
   * Reset reconnection attempts and try to connect
   */
  reconnect(): void {
    this.reconnectAttempts = 0
    this.connect()
  }
}

/**
 * Create a new SSE service instance
 */
export function createSSEService(url: string, options?: SSEOptions): SSEService {
  return new SSEService(url, options)
}
